import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'

import {
  ReductionTypeEnum,
  reductionTypeEnumMapping
} from '../../../../lib/types/enums/cse/reduction-type.enum'
import {
  CseReductionRule,
  CseReductionRuleInput
} from '../../../../lib/types/models/cse/cse-reduction-rule'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../../elements/country-flag'

/**
 * Groups consecutive numbers into ranges
 * @example
 * groupDurations([1, 2, 4, 6, 7, 8, 9, 12]) // returns "1 à 2, 4 à 9, 12"
 * groupDurations([5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]) // returns "5 à 15"
 */
export function getGroupDurations(durations: number[]): string[] {
  if (!durations || durations.length === 0) return []

  // Sort and remove duplicates
  const sorted = [...new Set(durations)].sort((a, b) => a - b)

  const ranges: string[] = []
  let rangeStart = sorted[0]
  let rangeEnd = sorted[0]

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === rangeEnd + 1) {
      // Continue the current range
      rangeEnd = sorted[i]
    } else {
      // End current range and start a new one
      ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart} à ${rangeEnd}`)
      rangeStart = sorted[i]
      rangeEnd = sorted[i]
    }
  }

  // Don't forget the last range
  ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart} à ${rangeEnd}`)

  return ranges
}

export const cseReductionTableColumns: Column<CseReductionRule>[] = [
  {
    header: 'Nom de la règle',
    accessorKey: 'name'
  },
  {
    header: 'Type',
    accessorKey: 'type',
    cell: ({ row }) => reductionTypeEnumMapping[row.original.type]
  },
  {
    header: 'Réduction',
    accessorKey: 'reduction',
    cell: ({ row }) => {
      const isPercentage = row.original.type === ReductionTypeEnum.PERCENTAGE
      return isPercentage ? `${row.original.reduction} %` : `${row.original.reduction} €`
    }
  },
  {
    header: 'Pays',
    accessorKey: 'countries',
    cell: ({ row }) => {
      if (row.original.countries.length === 0) return '-'

      return (
        <div className="flex gap-1 flex-wrap">
          {row.original.countries.map((country) => (
            <CountryFlag key={country.code} code={country.code} />
          ))}
        </div>
      )
    }
  },
  {
    header: 'Formules',
    accessorKey: 'formulas',
    cell: ({ row }) => {
      if (row.original.formulas.length === 0) return '-'
      return (
        <div className="text-balance break-all">
          {row.original.formulas.map((formula) => getTranslatedName(formula)).join(', ')}
        </div>
      )
    }
  },
  {
    header: 'Durée (jours)',
    accessorKey: 'journeyDuration',
    enableSorting: true,
    cell: ({ row }) => {
      const duration = row.original.journeyDuration
      const groupedDurations = getGroupDurations(duration)
      return groupedDurations.length > 0
        ? groupedDurations.map((group, index) => (
            <Badge key={index} text={group} className="mr-1 mb-1" />
          ))
        : '-'
    }
  },
  {
    header: 'Produits exclus',
    accessorKey: 'excludedProducts',
    cell: ({ row }) => {
      const excludedProducts = row.original.excludedProducts
      return excludedProducts.length > 0
        ? excludedProducts.map((product) => (
            <Badge key={product.id} variant="primary" text={product.name} className="mb-1" />
          ))
        : '-'
    }
  }
]

export const mappingCseReductionRuleToInput = (rule: CseReductionRule): CseReductionRuleInput => {
  return {
    name: rule.name,
    type: rule.type,
    reduction: rule.reduction,
    journeyDuration: rule.journeyDuration,
    countryIds: rule.countries.map((country) => country.id),
    formulaIds: rule.formulas.map((formula) => formula.id),
    excludedProductIds: rule.excludedProducts.map((product) => product.id)
  }
}
